import { Radio, Table } from "@mui/material";
import { Dispatch, ReactNode, SetStateAction } from "react";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import theme from "../../../../../../../theme";
import { replaceUnderScoreAddSpace } from "../../../../../../../utils/utils";
import {
  Assessment,
  AssessmentQuestion,
  AssessmentSection,
  ScreeningKeys,
} from "./constant-ass";

interface AssessmentTableProps {
  sections: AssessmentSection | null;
  setData: Dispatch<SetStateAction<Record<ScreeningKeys, Assessment> | null>>;
  selectedScreening: ScreeningKeys | null;
  setPayloadData: Dispatch<
    SetStateAction<Record<ScreeningKeys, Assessment> | undefined>
  >;
}

/**
 *
 * @returns Assessment Table Component Builder
 *
 * @author [Rajkumar Rathod]
 */
const AssessmentTableBuilder = ({
  sections,
  setData,
  selectedScreening,
  setPayloadData,
}: AssessmentTableProps) => {
  /* @example:  {"qn:score"} */
  const answersOptions = sections?.answersOptions;

  const handleRadioChange = (qnInd: number, selectedAnswer: string) => {
    setData((prev) => {
      if (!prev || !selectedScreening) return null;

      const copiedData = { ...prev };

      const currentAssessment = copiedData?.[selectedScreening];

      if (!currentAssessment) return null;

      if (currentAssessment?.section?.questions?.[qnInd]) {
        const currentQuestion = currentAssessment?.section?.questions[qnInd];
        const previousAnswer = currentQuestion.selectedAns;

        /* calculate the total score */
        const prevTotalScore = currentAssessment?.total_score ?? 0;
        const newTotalScore =
          prevTotalScore +
          (answersOptions?.[selectedAnswer] ?? 0) -
          (previousAnswer ? (answersOptions?.[previousAnswer] ?? 0) : 0);

        /* update the total score */
        currentAssessment.total_score = newTotalScore;

        if (newTotalScore > 0) {
          setPayloadData((prev) => ({
            ...((prev ?? {}) as any),
            [selectedScreening as ScreeningKeys]: currentAssessment as any,
          }));
        }

        /* update the selected answer */
        currentQuestion.selectedAns = selectedAnswer || "";
      }

      return copiedData;
    });
  };

  return (
    <Flex width={"100%"} height={"100%"}>
      <Table style={{ border: "1px solid #E0E0E0" }}>
        <thead style={{ border: "1px solid #E0E0E0", marginBottom: "10px" }}>
          <TableHeader
            title={sections?.title}
            answersOptions={answersOptions}
          />
        </thead>
        <tbody>
          <Rows
            qns={sections?.questions ?? []}
            answersOptions={answersOptions ?? {}}
            handleRadioChange={handleRadioChange}
          />
        </tbody>
      </Table>
    </Flex>
  );
};

export default AssessmentTableBuilder;

type RowsProps = {
  qns: AssessmentQuestion[];
  answersOptions: Record<string, number>;
  handleRadioChange: (qnInd: number, selectedAnswer: string) => void;
};

const Rows = ({ qns, answersOptions, handleRadioChange }: RowsProps) => {
  return qns?.map((qn: AssessmentQuestion, qnInd: number): ReactNode => (
    <tr key={qn.uuid} style={{ border: "1px solid #E0E0E0" }}>
      <td width={"2%"}>{qnInd + 1 + ". "}</td>
      <td width={"70%"}>
        <Flex gap={1}>
          <Text>{qn?.title}</Text>
        </Flex>
      </td>

      {Object.keys(answersOptions)?.map((curOption: string) => (
        <td key={curOption} width={"10%"}>
          <Flex alignItems={"center"} justifyContent={"center"}>
            <Radio
              checked={qn.selectedAns === curOption}
              value={curOption}
              onChange={() => handleRadioChange?.(qnInd, curOption)}
            />
          </Flex>
        </td>
      ))}
    </tr>
  ));
};

type TableHeaderProps = {
  title?: string;
  answersOptions?: Record<string, number>;
};

const TableHeader = ({ title, answersOptions }: TableHeaderProps) => {
  return (
    <tr
      style={{
        backgroundColor: theme.palette.info.light,
        minHeight: "60px",
        height: "60px",
      }}
    >
      <th></th>

      {/* qn */}
      <th>
        <Text fontWeight={"bold"}>{title}</Text>
      </th>

      {/* options */}
      {Object.keys(answersOptions ?? {})?.map((it: string) => (
        <th key={it}>
          <Flex
            alignItems={"center"}
            minWidth={"100px"}
            justifyContent={"center"}
          >
            <Text>{replaceUnderScoreAddSpace(it?.toLowerCase() || "")}</Text>
          </Flex>
        </th>
      ))}
    </tr>
  );
};
